









































































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import {
    documentTypes,
    formatDocumentType,
    DOCUMENT_TYPE,
} from '@/data/documentTypes';
import Program from '@/models/Program';
import ProgramDocument from '@/models/ProgramDocument';
import {
    getProgramArchivePrescriber,
} from '@/api/api';
import { saveAs } from 'file-saver';
import AuthModule from '@/store/AuthModule';
import { BACKEND_URL } from '@/helpers/constants';
import PdfThumbnail from './PdfThumbnail.vue';

interface DocItem {
    id: number;
    label: string;
    rawType: DOCUMENT_TYPE;
    extension: string;
    url: string;
    icon: string;
    colorClass: string;
}

interface DocumentsByType {
    [index: string]: ProgramDocument[];
}

// Full type order for building allDocuments
const TYPE_ORDER: DOCUMENT_TYPE[] = [
    'documents_commercial_brochure',
    'documents_works_contract',
    'documents_booking_contract',
    'documents_notice',
    'documents_notice_pro',
    // 'documents_global_plan' hidden for prescribers (booking contracts / risk docs)
    'documents_floor_plan',
    'documents_parking_plan',
    'documents_management_brochure',
    'documents_rental_agreement',
    'documents_annual_charge',
    'documents_general_assembly',
    'documents_autres',
];

// Priority types shown first in the initial (pre-"Voir plus") grid
const PRIORITY_TYPES: DOCUMENT_TYPE[] = [
    'documents_notice',
    'documents_booking_contract',
    'documents_works_contract',
];

@Component({
    filters: { formatDocumentType },
    components: { PdfThumbnail },
})
export default class ProgramDocuments extends Vue {
    @Prop() program!: Program | null;

    showConfirm  = false;
    downloadingAll = false;
    fileSizes: Record<number, string> = {};
    showAll     = false;
    loadingMore = false;
    downloadProgressModal = false;
    downloadProgressStep  = '';

    get isTrial() { return AuthModule.isTrialActive; }
    get isSub()   { return AuthModule.isSubscriptionActive; }

    // One doc per type, priority types first, then the rest of TYPE_ORDER
    get initialDocuments(): DocItem[] {
        const priorityFirst: DOCUMENT_TYPE[] = [
            ...PRIORITY_TYPES,
            ...TYPE_ORDER.filter(
                (t: DOCUMENT_TYPE) => PRIORITY_TYPES.indexOf(t) === -1
            ),
        ];
        const result: DocItem[] = [];
        for (const type of priorityFirst) {
            const typeDocs = this.allDocuments.filter(
                (d: DocItem) => d.rawType === type
            );
            if (typeDocs.length > 0) result.push(typeDocs[0]);
        }
        return result;
    }

    // Every doc that is NOT in initialDocuments
    get remainingDocuments(): DocItem[] {
        const initialIds: number[] = this.initialDocuments.map(
            (d: DocItem) => d.id
        );
        return this.allDocuments.filter(
            (d: DocItem) => initialIds.indexOf(d.id) === -1
        );
    }

    // What the grid actually renders
    get displayedDocuments(): DocItem[] {
        return this.showAll
            ? this.allDocuments
            : this.initialDocuments;
    }

    get docsByType(): DocumentsByType {
        return Object.keys(documentTypes).reduce<DocumentsByType>((acc, key) => {
            acc[key] = (this.program && this.program.documents)
                ? this.program.documents.filter((d: ProgramDocument) => d.type === key)
                : [];
            return acc;
        }, {});
    }

    get allDocuments(): DocItem[] {
        const items: DocItem[] = [];
        for (const type of TYPE_ORDER) {
            const docs: ProgramDocument[] = this.docsByType[type] || [];
            docs.forEach((doc: ProgramDocument, idx: number) => {
                const typeName = formatDocumentType(type);
                const label = docs.length > 1 ? `${typeName} ${idx + 1}` : typeName;
                const ext = doc.document.extension.toLowerCase();
                items.push({
                    id: doc.id,
                    label,
                    rawType: type,
                    extension: doc.document.extension,
                    url: `${BACKEND_URL}${doc.document.url}${doc.document.name}.${doc.document.extension}`,
                    icon: this.getFileIcon(ext),
                    colorClass: this.getColorClass(ext),
                });
            });
        }
        return items;
    }

    mounted() {
        this.fetchFileSizes(this.initialDocuments);
    }

    @Watch('program')
    onProgramChange() {
        this.showAll     = false;
        this.loadingMore = false;
        this.fetchFileSizes(this.initialDocuments);
    }

    handleVoirPlus() {
        if (this.loadingMore) return;
        this.loadingMore = true;
        // Let ghost cards render for one frame, then reveal real cards
        this.$nextTick(() => {
            setTimeout(() => {
                this.showAll     = true;
                this.loadingMore = false;
                this.$nextTick(() => {
                    this.fetchFileSizes(this.remainingDocuments);
                });
            }, 420);
        });
    }

    async fetchFileSizes(docs: DocItem[]) {
        for (const doc of docs) {
            if (this.fileSizes[doc.id]) continue;
            try {
                const res = await fetch(doc.url, { method: 'HEAD' });
                const cl = res.headers.get('content-length');
                if (cl) {
                    this.$set(this.fileSizes, doc.id, this.formatBytes(parseInt(cl)));
                }
            } catch { /* CORS or network – size stays hidden */ }
        }
    }

    formatBytes(bytes: number): string {
        if (bytes < 1024) return `${bytes} o`;
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    }

    openDocument(doc: DocItem) {
        window.open(doc.url, '_blank', 'noopener,noreferrer');
    }

    async downloadAll() {
        if (!this.program || !this.program.id) return;
        if (this.isTrial && !this.isSub) {
            this.showConfirm = true;
            return;
        }
        const programId = this.program.id as number;
        const programName = this.program.name;

        this.downloadingAll       = true;
        this.downloadProgressModal = true;
        this.downloadProgressStep  = 'Collecte des documents…';

        try {
            await new Promise(r => setTimeout(r, 400));
            this.downloadProgressStep = 'Compression de l\'archive ZIP…';
            const { data } = await getProgramArchivePrescriber(programId);
            this.downloadProgressStep = 'Téléchargement en cours…';
            await new Promise(r => setTimeout(r, 500));
            saveAs(data, `${programName}.zip`);
        } catch {
            (this as any).$vs.notify({
                color: 'danger',
                position: 'top-right',
                title: 'Erreur',
                text: 'Une erreur est survenue lors de la préparation de l\'archive.',
            });
        }

        this.downloadProgressStep = 'Terminé !';
        await new Promise(r => setTimeout(r, 800));
        this.downloadingAll        = false;
        this.downloadProgressModal = false;
        this.downloadProgressStep  = '';
    }

    getFileIcon(ext: string): string {
        if (ext === 'pdf') return 'fas fa-file-pdf';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'fas fa-file-image';
        if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word';
        if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fas fa-file-excel';
        if (['ppt', 'pptx'].includes(ext)) return 'fas fa-file-powerpoint';
        if (['zip', 'rar', '7z'].includes(ext)) return 'fas fa-file-archive';
        return 'fas fa-file-alt';
    }

    getColorClass(ext: string): string {
        if (ext === 'pdf') return 'pdf';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
        if (['doc', 'docx'].includes(ext)) return 'word';
        if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
        if (['ppt', 'pptx'].includes(ext)) return 'ppt';
        return 'default';
    }
}
